import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ComposePaypalViewModel from './viewModel';
import APIServices from '../../../utils/services/EveryDoggyAPIServices';
import errorLogger from '../../../utils/errorLogger/appErrorLogger';
import { toggleLoader } from '../../../reduxStore/navigation/actions';
import { RootState } from '../../../store';
import {
  analyticsEventsTracker,
  EVENT_TRACKERS,
  GTMOnboardingFunnelEvent,
  GTMQuizFunnelEvent,
} from '../../../utils/analytics';
import { getLocalizedError } from '../../../utils/scripts/getLocalizedError';
import { getOnboardingAnswersFromURL } from '../../../utils/scripts';
import { capturePaymentError } from '../../../utils/payments/paymentErrorHandler';
import { usePaypalRenderGate } from '../../../utils/payments/paypalRenderGate';
import { extractEmailFromUrl } from '../../../utils/scripts/extractEmailFromUrl';
import { CreatePaypalSubscriptionInput, OnboardingAnswers } from './types';

import payPalIcon from './img/payPalIcon.png';

const PayPalButton = styled.button`
  width: 100%;
  height: 55px;
  border: none;
  background: transparent;
  color: #001c64;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.08px;
  transition: opacity 0.2s ease;

  img {
    height: 24px;
    max-height: 24px;
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid #003087;
    outline-offset: 2px;
  }
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid #001c64;
  border-right-color: transparent;
  border-radius: 50%;
  animation: paypal-v5-spin 0.8s linear infinite;

  @keyframes paypal-v5-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const errorWithMessage = error as { message?: string };
    if (errorWithMessage.message) {
      return errorWithMessage.message;
    }
  }

  return 'Unknown PayPal error';
};

const setPageScrollLocked = (isLocked: boolean) => {
  const htmlNode = document.querySelector('html') as HTMLElement | null;
  if (!htmlNode) {
    return;
  }

  if (isLocked) {
    htmlNode.classList.add('disableScroll');
    return;
  }

  htmlNode.classList.remove('disableScroll');
};

const clearPaypalWarning = () => {
  const paypalWarnings = document.querySelector('.paypalWarnings') as HTMLElement | null;
  if (!paypalWarnings) {
    return;
  }

  paypalWarnings.innerHTML = '';
  paypalWarnings.style.display = 'none';
};

const showPaypalWarning = (message: string) => {
  const paypalWarnings = document.querySelector('.paypalWarnings') as HTMLElement | null;
  if (!paypalWarnings) {
    return;
  }

  paypalWarnings.classList.remove('disableScroll');
  paypalWarnings.style.display = 'block';
  paypalWarnings.innerHTML = message;
};

export const SubscriptionPaypalButtons: FC = () => {
  const dispatchReduxState = useDispatch();
  const [viewModel] = useState(() =>
    ComposePaypalViewModel(window.location.href, APIServices, errorLogger)
  );

  const productDetails = useSelector((state: RootState) => state.data.paymentModal.product.details);
  const selectedProductIdFromState = useSelector((state: RootState) => state.data.paymentModal.product?.name || '');
  const paypalPlanId = productDetails?.paypal_plan_id || '';
  const paypalAmount = productDetails?.trial_amount || productDetails?.amount || '';
  const paypalName = useSelector((state: RootState) => state.data.paymentModal.productName?.name || '');
  const internalId = productDetails?.internal_product_id || '';
  const productId = productDetails?.id || selectedProductIdFromState || '';
  const productOldPrice = productDetails?.oldPrice || '';
  const tariff = useSelector((state: RootState) => state.data.tariff || '');
  const discountShownOnce = useSelector((state: RootState) => state.navigation.discountShownOnce);
  const showChatUpsale = useSelector((state: RootState) => state.navigation.purchaseFlowScreen.showChatUpsale);

  const [emailFromUrl] = useState<string>(() => extractEmailFromUrl());
  const [answersFromUrl] = useState<OnboardingAnswers>(() => getOnboardingAnswersFromURL());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showLoader = (isVisible: boolean) => {
    dispatchReduxState(toggleLoader(isVisible));
  };

  usePaypalRenderGate({
    checks: [
      { value: paypalPlanId, reason: 'missing_paypal_plan_id' },
      { value: emailFromUrl, reason: 'missing_email' },
    ],
    extra: { productName: paypalName },
  });

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      const navigationEntries = window.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const navigationType = navigationEntries[0]?.type;
      const restoredFromBackForward = event.persisted || navigationType === 'back_forward';

      if (!restoredFromBackForward) {
        return;
      }

      setIsSubmitting(false);
      showLoader(false);
      setPageScrollLocked(false);
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const trackCreateSubscriptionIntent = () => {
    if (showChatUpsale) {
      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paymentIntentionChat(
          paypalName,
          'paypal',
          `${paypalAmount}`
        ),
        EVENT_TRACKERS.GTM
      );
      return;
    }

    analyticsEventsTracker.trackWithTarget(
      GTMQuizFunnelEvent.tryPaid({
        paymentType: 'paypal',
        trialPrice: `${paypalAmount}`,
        productName: paypalName,
      }),
      EVENT_TRACKERS.GTM
    );
  };

  const handleClick = async () => {
    if (isSubmitting) {
      return;
    }

    if (!paypalPlanId) {
      const missingPlanIdMessage = 'An error occurred while processing your card. Try again in a little bit.';
      capturePaymentError({
        provider: 'paypal',
        stage: 'create_subscription',
        errorName: 'PayPalPlanIdMissingError',
        message: missingPlanIdMessage,
        extra: {
          productName: paypalName,
          amount: paypalAmount,
        },
      });
      showPaypalWarning(getLocalizedError(missingPlanIdMessage));
      return;
    }

    setIsSubmitting(true);
    showLoader(true);
    setPageScrollLocked(true);
    clearPaypalWarning();

    try {
      trackCreateSubscriptionIntent();

      const createSubscriptionInput: CreatePaypalSubscriptionInput = {
        paypalPlanId,
        paypalAmount,
        paypalName,
        internalId,
        productId,
        productOldPrice,
        redirectFlow: showChatUpsale ? 'chat_upsale' : 'default',
        tariff,
        discountShownOnce,
        answers: answersFromUrl,
      };

      const result = await viewModel.createSubscriptionAction(createSubscriptionInput);

      window.location.assign(result.approvalLink);
    } catch (error: unknown) {
      const errorName = 'PayPalCreateSubscriptionV2Error';
      const errorMessage = getErrorMessage(error);

      setIsSubmitting(false);
      showLoader(false);
      setPageScrollLocked(false);
      showPaypalWarning(getLocalizedError(errorMessage));

      capturePaymentError({
        provider: 'paypal',
        stage: 'create_subscription',
        errorName,
        message: errorMessage,
        extra: {
          paypal_plan_id: paypalPlanId,
          internal_id: internalId,
        },
      });

      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paypalError(errorName, emailFromUrl || '', internalId),
        EVENT_TRACKERS.GTM
      );
      window.dataLayer?.push({
        event: 'paypal_error',
        error_name: errorName,
        email: emailFromUrl || '',
        product_name: internalId,
      });

      const reportableError = new Error(errorMessage);
      reportableError.name = errorName;
      await viewModel.reportPaymentError(reportableError);
    }
  };

  return (
    <PayPalButton
      type="button"
      onClick={handleClick}
      disabled={isSubmitting}
      aria-label="Confirm purchase with PayPal"
    >
      {isSubmitting ? <Spinner aria-hidden="true" /> : <img src={payPalIcon} alt="paypal" /> }
    </PayPalButton>
  );
};
